import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Table, Form, Spinner, InputGroup, Row, Col, Badge } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { registerLocale } from 'react-datepicker';
import es from 'date-fns/locale/es';
registerLocale('es', es);
import { debounce } from 'lodash';
import useToken from 'hooks/useToken';

// Format date to readable Spanish format
const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    return `${date.getDate()} de ${months[date.getMonth()]} ${date.getFullYear()}`;
};

const AkasiaPosUsers = () => {
    const { token } = useToken();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [userType, setUserType] = useState('All');
    const [userStatus, setUserStatus] = useState('All');
    const [dateFilterType, setDateFilterType] = useState('LastConnection');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const tableContainerRef = useRef(null);
    const pageSize = 25;

    const fetchData = useCallback(async (page, search, type, status, dateType, start, end, append = false) => {
        if (!token) return;

        try {
            if (append) {
                setLoadingMore(true);
            } else {
                setLoading(true);
            }

            const response = await fetch('/api/AkasiaPosDashboard/GetUsers', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    pageNumber: page,
                    pageSize: pageSize,
                    searchTerm: search || null,
                    userType: type,
                    userStatus: status,
                    dateFilterType: dateType,
                    startDate: start ? start.toISOString() : null,
                    endDate: end ? end.toISOString() : null
                })
            });

            const result = await response.json();
            if (result.iResponseType === 1 && result.detalles) {
                const parsed = JSON.parse(result.detalles);

                // Always prefer table2 for data records
                const dataTable = parsed.table2 || [];

                if (dataTable.length > 0) {
                    if (append) {
                        setData(prev => [...prev, ...dataTable]);
                    } else {
                        setData(dataTable);
                    }

                    setHasMore(page * pageSize < (dataTable[0]?.iTotalCount || 0));
                } else {
                    if (!append) {
                        setData([]);
                    }
                    setHasMore(false);
                }
            } else {
                setError('Error loading users');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [token, pageSize]);

    // Debounced search
    const debouncedFetch = useCallback(
        debounce((search, type, status, dateType, start, end) => {
            setPageNumber(1);
            setData([]);
            fetchData(1, search, type, status, dateType, start, end, false);
        }, 500),
        [fetchData]
    );

    // Initial load and filter changes
    useEffect(() => {
        if (token) {
            debouncedFetch(searchTerm, userType, userStatus, dateFilterType, startDate, endDate);
        }
    }, [token, searchTerm, userType, userStatus, dateFilterType, startDate, endDate, debouncedFetch]);

    // Infinite scroll handler
    const handleScroll = useCallback(() => {
        const container = tableContainerRef.current;
        if (!container || loadingMore || !hasMore) return;

        const scrollPercentage = (container.scrollTop + container.clientHeight) / container.scrollHeight;
        if (scrollPercentage > 0.8) {
            const nextPage = pageNumber + 1;
            setPageNumber(nextPage);
            fetchData(nextPage, searchTerm, userType, userStatus, dateFilterType, startDate, endDate, true);
        }
    }, [loadingMore, hasMore, pageNumber, searchTerm, userType, userStatus, dateFilterType, startDate, endDate, fetchData]);

    useEffect(() => {
        const container = tableContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            return () => container.removeEventListener('scroll', handleScroll);
        }
    }, [handleScroll]);

    return (
        <Card className="shadow-sm border-0">
            <Card.Header className="bg-white border-bottom">
                <div className="mb-3">
                    <h5 className="mb-1 fw-bold">
                        <i className="bi bi-people-fill text-primary me-2"></i>
                        Usuarios
                    </h5>
                    <p className="text-muted small mb-0">Gestiona usuarios su ultima conexion y estado</p>
                </div>

                {/* Filters Row */}
                <Row className="g-2 mb-3">
                    <Col md={2}>
                        <Form.Select
                            size="sm"
                            value={userType}
                            onChange={(e) => setUserType(e.target.value)}
                            className="border-primary"
                        >
                            <option value="All">Tipos: Todos</option>
                            <option value="Trial">Prueba</option>
                            <option value="Paid">Pagado</option>
                        </Form.Select>
                    </Col>
                    <Col md={2}>
                        <Form.Select
                            size="sm"
                            value={userStatus}
                            onChange={(e) => setUserStatus(e.target.value)}
                            className="border-success"
                        >
                            <option value="All">Estado: Todos</option>
                            <option value="Active">Activo</option>
                            <option value="Inactive">Inactivo</option>
                        </Form.Select>
                    </Col>
                    <Col md={2}>
                        <InputGroup size="sm">
                            <InputGroup.Text className="bg-light border-warning">
                                <i className="bi bi-filter text-muted"></i>
                            </InputGroup.Text>
                            <Form.Select
                                value={dateFilterType}
                                onChange={(e) => setDateFilterType(e.target.value)}
                                className="border-warning"
                            >
                                <option value="LastConnection">Última Conexión</option>
                                <option value="CreationDate">Fecha Creación</option>
                            </Form.Select>
                        </InputGroup>
                    </Col>
                    <Col md={2} style={{ position: 'relative', zIndex: 1050 }}>
                        <DatePicker
                            selected={startDate}
                            onChange={(date) => setStartDate(date)}
                            className="form-control form-control-sm"
                            dateFormat="dd 'de' MMMM yyyy"
                            locale="es"
                            placeholderText="Desde"
                            todayButton="Hoy"
                            isClearable
                            popperPlacement="bottom-start"
                        />
                    </Col>
                    <Col md={2} style={{ position: 'relative', zIndex: 1050 }}>
                        <DatePicker
                            selected={endDate}
                            onChange={(date) => setEndDate(date)}
                            className="form-control form-control-sm"
                            dateFormat="dd 'de' MMMM yyyy"
                            locale="es"
                            placeholderText="Hasta"
                            todayButton="Hoy"
                            isClearable
                            popperPlacement="bottom-start"
                        />
                    </Col>
                    <Col md={2}>
                        <InputGroup size="sm">
                            <InputGroup.Text className="bg-white border-end-0">
                                <i className="bi bi-search text-muted"></i>
                            </InputGroup.Text>
                            <Form.Control
                                type="text"
                                placeholder="Buscar..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="border-start-0 ps-0"
                            />
                        </InputGroup>
                    </Col>
                </Row>
            </Card.Header>
            <Card.Body className="p-0">
                <div
                    ref={tableContainerRef}
                    className="table-responsive"
                    style={{ maxHeight: '600px', overflowY: 'auto' }}
                >
                    <Table hover className="mb-0">
                        <thead className="bg-light sticky-top" style={{ top: 0, zIndex: 1 }}>
                            <tr>
                                <th className="ps-4 py-3 border-bottom">ID Cliente</th>
                                <th className="py-3 border-bottom">Nombre</th>
                                <th className="py-3 border-bottom">Email</th>
                                <th className="py-3 border-bottom">Empresa</th>
                                <th className="py-3 border-bottom">Tipo</th>
                                <th className="py-3 border-bottom">Estado</th>
                                <th className="py-3 border-bottom">Fin Licencia</th>
                                <th className="py-3 border-bottom">Última Actividad</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && data.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="text-center py-5">
                                        <Spinner animation="border" variant="primary" />
                                        <p className="text-muted mt-2 mb-0">Cargando usuarios...</p>
                                    </td>
                                </tr>
                            ) : error ? (
                                <tr>
                                    <td colSpan="8" className="text-center py-5">
                                        <i className="bi bi-exclamation-circle text-danger fs-1"></i>
                                        <p className="text-danger mt-2 mb-0">{error}</p>
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="text-center py-5">
                                        <i className="bi bi-inbox text-muted fs-1"></i>
                                        <p className="text-muted mt-2 mb-0">No se encontraron usuarios</p>
                                    </td>
                                </tr>
                            ) : (
                                <>
                                    {data.map((item, index) => (
                                        <tr key={`${item.vUsername}-${index}`}>
                                            <td className="ps-4 py-3">
                                                <a href={`/clientes/detalle/${item.idClienteAkasia}`} className="text-decoration-none">
                                                    <Badge bg="light" text="dark" className="border">
                                                        {item.idClienteAkasia || 'N/A'}
                                                    </Badge>
                                                </a>
                                            </td>
                                            <td className="py-3">
                                                <div className="fw-bold">
                                                    <a href={`/clientes/detalle/${item.idClienteAkasia}`} className="text-decoration-none text-dark">
                                                        {item.vFirstName && item.vLastName
                                                            ? `${item.vFirstName} ${item.vLastName}`
                                                            : item.vUsername}
                                                    </a>
                                                </div>
                                                {item.bIsSuperAdmin && (
                                                    <Badge bg="danger" className="mt-1">
                                                        <i className="bi bi-shield-fill me-1"></i>
                                                        Super Admin
                                                    </Badge>
                                                )}
                                            </td>
                                            <td className="py-3">
                                                <div className="d-flex flex-column">
                                                    <span className="text-dark">{item.vEmail}</span>
                                                    {item.vPassword && (
                                                        <span className="text-muted small mt-1" style={{ fontSize: '0.75rem' }}>
                                                            <i className="bi bi-key me-1"></i>
                                                            {item.vPassword}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="py-3">
                                                <span className="fw-medium">{item.vNombreEmpresa || 'N/A'}</span>
                                            </td>
                                            <td className="py-3">
                                                {item.vTipoUsuario === 'Trial' ? (
                                                    <Badge bg="warning" text="dark">
                                                        <i className="bi bi-clock-history me-1"></i>
                                                        Prueba
                                                    </Badge>
                                                ) : (
                                                    <Badge bg="success">
                                                        <i className="bi bi-check-circle me-1"></i>
                                                        Pagado
                                                    </Badge>
                                                )}
                                            </td>
                                            <td className="py-3">
                                                {item.bIsActive ? (
                                                    <Badge bg="success">
                                                        <i className="bi bi-check-circle me-1"></i>
                                                        Activo
                                                    </Badge>
                                                ) : (
                                                    <Badge bg="secondary">
                                                        <i className="bi bi-x-circle me-1"></i>
                                                        Inactivo
                                                    </Badge>
                                                )}
                                            </td>
                                            <td className="py-3">
                                                {item.dFinLicenciaUso ? (
                                                    <span className={new Date(item.dFinLicenciaUso) < new Date() ? 'text-danger fw-bold' : 'text-muted'}>
                                                        {formatDate(item.dFinLicenciaUso)}
                                                    </span>
                                                ) : (
                                                    <span className="text-muted">-</span>
                                                )}
                                            </td>
                                            <td className="py-3">
                                                {item.dUltimaActividad ? (
                                                    <span className="text-muted small">
                                                        <i className="bi bi-clock me-1"></i>
                                                        {formatDate(item.dUltimaActividad)}
                                                    </span>
                                                ) : (
                                                    <Badge bg="secondary">Nunca</Badge>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </>
                            )}
                        </tbody>
                    </Table>
                    {loadingMore && (
                        <div className="text-center py-3 border-top">
                            <Spinner animation="border" size="sm" variant="primary" className="me-2" />
                            <span className="text-muted small">Cargando más...</span>
                        </div>
                    )}
                </div>
            </Card.Body>
            {!loading && data.length > 0 && (
                <Card.Footer className="bg-light text-center text-muted small">
                    {hasMore ? (
                        <>
                            <i className="bi bi-arrow-down me-1"></i>
                            Desplázate para ver más
                        </>
                    ) : (
                        <>
                            <i className="bi bi-check-circle me-1"></i>
                            Todos los usuarios cargados
                        </>
                    )}
                </Card.Footer>
            )}
        </Card>
    );
};

export default AkasiaPosUsers;
